import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useProgress } from '../../contexts/ProgressContext';
import { useNavigate } from 'react-router-dom';
import ProgressBar from '../../components/ProgressBar';
import SectionCheckbox from '../../components/SectionCheckbox';
import Quiz from '../../components/Quiz';
import { PromptBox } from '../../components/CopyButton';
import PromptPlayground from '../../components/PromptPlayground';
import StepByStepGuide from '../../components/StepByStepGuide';
import DiscordWidget from '../../components/DiscordWidget';
import '../Dashboard.css';
import './Module.css';

const Module3 = () => {
  const { user, isAuthenticated, logout } = useAuth();
  const { progress } = useProgress();
  const navigate = useNavigate();
  const [showQuiz, setShowQuiz] = useState(false);

  if (!isAuthenticated) { navigate('/login'); return null; }

  const handleLogout = () => { logout(); navigate('/'); };
  const totalProgress = progress?.totalProgress || 0;

  const quizQuestions = [
    {
      question: "Quel est l'avantage principal de Claude Code par rapport à ChatGPT pour le développement ?",
      options: [
        "Il est plus rapide",
        "Il peut exécuter du code directement et modifier des fichiers",
        "Il est gratuit",
        "Il connaît plus de langages"
      ],
      correctAnswer: 1
    },
    {
      question: "Quelle est la meilleure façon de décrire un projet à Claude Code ?",
      options: [
        "Donner juste le nom du projet",
        "Écrire un brief détaillé avec fonctionnalités, design et parcours utilisateur",
        "Envoyer uniquement des captures d'écran",
        "Lui demander de deviner ce que vous voulez"
      ],
      correctAnswer: 1
    },
    {
      question: "Que faut-il faire quand Claude Code génère une erreur ?",
      options: [
        "Abandonner le projet",
        "Recommencer depuis zéro",
        "Copier l'erreur et lui demander de la corriger",
        "Ignorer l'erreur"
      ],
      correctAnswer: 2
    },
    {
      question: "Pour créer une application web complète, Claude Code a besoin de :",
      options: [
        "Uniquement le nom de l'application",
        "Un brief complet : fonctionnalités, design, parcours utilisateur",
        "Le code existant obligatoirement",
        "Une formation en programmation"
      ],
      correctAnswer: 1
    },
    {
      question: "Quelle est la règle d'or pour travailler avec Claude Code ?",
      options: [
        "Écrire le moins possible",
        "Ne jamais tester le code",
        "Décrire ce que vous voulez, pas comment le faire",
        "Toujours coder vous-même d'abord"
      ],
      correctAnswer: 2
    }
  ];

  const playgroundConfig = {
    title: "Brief Projet pour Claude Code",
    description: "Créez un brief complet pour votre projet",
    templatePrompt: `Crée une application {{type}} appelée "{{name}}".

DESCRIPTION :
{{description}}

FONCTIONNALITÉS PRINCIPALES :
1. {{feature1}}
2. {{feature2}}
3. {{feature3}}

PARCOURS UTILISATEUR :
1. L'utilisateur arrive sur {{landing}}
2. Il peut {{action1}}
3. Ensuite il {{action2}}
4. Résultat : {{result}}

DESIGN :
- Style : {{style}}
- Couleurs : {{colors}}

CONTRAINTES :
- {{constraint}}`,
    variables: [
      { name: 'type', label: 'Type d\'application', type: 'select', options: [
        { value: 'web React', label: 'Web App (React)' },
        { value: 'site vitrine', label: 'Site vitrine' },
        { value: 'dashboard', label: 'Dashboard' },
        { value: 'e-commerce', label: 'E-commerce' }
      ], required: true },
      { name: 'name', label: 'Nom du projet', placeholder: 'Ex: TaskMaster', type: 'text', required: true },
      { name: 'description', label: 'Description en 1-2 phrases', placeholder: 'Ex: Une app de gestion de tâches...', type: 'textarea', required: true },
      { name: 'feature1', label: 'Fonctionnalité 1', placeholder: 'Ex: Authentification utilisateur', type: 'text', required: true },
      { name: 'feature2', label: 'Fonctionnalité 2', placeholder: 'Ex: Création de tâches', type: 'text', required: true },
      { name: 'feature3', label: 'Fonctionnalité 3', placeholder: 'Ex: Notifications', type: 'text', required: true },
      { name: 'landing', label: 'Page d\'arrivée', placeholder: 'Ex: la page d\'accueil avec login', type: 'text', required: true },
      { name: 'action1', label: 'Action principale 1', placeholder: 'Ex: se connecter ou créer un compte', type: 'text', required: true },
      { name: 'action2', label: 'Action principale 2', placeholder: 'Ex: accéder à son dashboard', type: 'text', required: true },
      { name: 'result', label: 'Résultat final', placeholder: 'Ex: il voit toutes ses tâches organisées', type: 'text', required: true },
      { name: 'style', label: 'Style visuel', type: 'select', options: [
        { value: 'moderne et minimaliste', label: 'Moderne / Minimaliste' },
        { value: 'corporate et professionnel', label: 'Corporate' },
        { value: 'coloré et fun', label: 'Coloré / Fun' },
        { value: 'dark mode luxe', label: 'Dark Mode Luxe' }
      ], required: true },
      { name: 'colors', label: 'Couleurs principales', placeholder: 'Ex: bleu #3B82F6 et blanc', type: 'text', required: true },
      { name: 'constraint', label: 'Contrainte technique', placeholder: 'Ex: Doit être responsive mobile', type: 'text', required: true }
    ]
  };

  const projectSteps = [
    {
      title: "Installer Claude Code",
      description: "La première étape est d'installer Claude Code sur votre machine.",
      code: `npm install -g @anthropic-ai/claude-code`,
      codeLanguage: "bash",
      tips: ["Vous devez avoir Node.js installé (v18+)", "Lancez cette commande dans votre terminal"],
      checkpoint: "Claude Code est installé si vous pouvez taper 'claude-code' dans le terminal"
    },
    {
      title: "Lancer Claude Code",
      description: "Naviguez vers votre dossier de projet et lancez Claude Code.",
      code: `cd mon-projet
claude-code`,
      codeLanguage: "bash",
      tips: ["Créez d'abord un dossier vide pour votre projet", "Claude Code va analyser le contexte du dossier"],
      checkpoint: "Vous voyez l'interface de Claude Code dans votre terminal"
    },
    {
      title: "Décrire votre projet",
      description: "Donnez un brief clair et complet de ce que vous voulez créer.",
      prompt: `Crée une application de liste de tâches (todo app).

Fonctionnalités :
- Ajouter une tâche avec un titre
- Marquer une tâche comme complétée
- Supprimer une tâche
- Les tâches sont sauvegardées localement

Design : moderne, minimaliste, fond sombre
Stack : React avec CSS moderne`,
      tips: ["Plus votre description est précise, meilleur sera le résultat", "Mentionnez le style visuel souhaité"],
      checkpoint: "Claude Code commence à créer les fichiers de votre projet"
    },
    {
      title: "Tester et itérer",
      description: "Lancez l'application et demandez des modifications.",
      code: `npm start`,
      codeLanguage: "bash",
      prompt: `L'application fonctionne mais je voudrais :
- Ajouter une animation quand on complète une tâche
- Changer la couleur du bouton "Ajouter" en vert
- Ajouter un compteur de tâches restantes`,
      tips: ["Testez chaque fonctionnalité", "Demandez des ajustements précis"],
      checkpoint: "Votre application fonctionne avec les modifications demandées"
    },
    {
      title: "Finaliser le projet",
      description: "Ajoutez les dernières touches et préparez le déploiement.",
      prompt: `Le projet est presque fini. Peux-tu :
1. Ajouter un favicon
2. Optimiser pour mobile (responsive)
3. Ajouter des meta tags pour le SEO
4. M'expliquer comment déployer sur Vercel`,
      tips: ["Pensez à l'expérience mobile", "Préparez les assets (favicon, images)"],
      checkpoint: "Votre application est prête à être déployée"
    }
  ];

  return (
    <div className="dashboard-layout">
      <aside className="dashboard-sidebar">
        <div className="sidebar-logo" onClick={() => navigate('/dashboard')}>
          <span className="logo-text">F12IA</span>
          <span className="logo-tm">™</span>
        </div>

        <div className="sidebar-user">
          <div className="user-avatar">
            {user?.firstName?.charAt(0).toUpperCase() || 'U'}
          </div>
          <div className="user-info">
            <span className="user-name">{user?.firstName || 'Utilisateur'}</span>
            <span className="user-role">Membre PRO</span>
          </div>
        </div>

        <nav className="sidebar-nav-pro">
          <div className="nav-label">MENU</div>
          <div className="nav-item" onClick={() => navigate('/dashboard')}>
            <span className="nav-icon"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/><rect x="3" y="14" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/></svg></span>
            <span className="nav-text">Tableau de bord</span>
          </div>

          <div className="nav-label">MODULES</div>
          <div className="nav-item" onClick={() => navigate('/module/1')}>
            <span className="nav-icon"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><path d="M12 16v-4M12 8h.01"/></svg></span>
            <span className="nav-text">1. Introduction IA</span>
          </div>
          <div className="nav-item" onClick={() => navigate('/module/2')}>
            <span className="nav-icon"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg></span>
            <span className="nav-text">2. ChatGPT</span>
          </div>
          <div className="nav-item active" onClick={() => navigate('/module/3')}>
            <span className="nav-icon"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="16 18 22 12 16 6"/><polyline points="8 6 2 12 8 18"/></svg></span>
            <span className="nav-text">3. Claude Code</span>
          </div>
          <div className="nav-item" onClick={() => navigate('/module/4')}>
            <span className="nav-icon"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="3"/><path d="M12 1v4M12 19v4M4.22 4.22l2.83 2.83M16.95 16.95l2.83 2.83M1 12h4M19 12h4M4.22 19.78l2.83-2.83M16.95 7.05l2.83-2.83"/></svg></span>
            <span className="nav-text">4. Automatisation</span>
          </div>
          <div className="nav-item" onClick={() => navigate('/module/5')}>
            <span className="nav-icon"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="2" y="3" width="20" height="14" rx="2"/><path d="M8 21h8M12 17v4"/></svg></span>
            <span className="nav-text">5. Créer des Apps</span>
          </div>
          <div className="nav-item" onClick={() => navigate('/module/6')}>
            <span className="nav-icon"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 2L2 7l10 5 10-5-10-5z"/><path d="M2 17l10 5 10-5"/><path d="M2 12l10 5 10-5"/></svg></span>
            <span className="nav-text">6. Projets Avancés</span>
          </div>

          <div className="nav-label">COMPTE</div>
          <div className="nav-item" onClick={() => navigate('/profile')}>
            <span className="nav-icon"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="8" r="4"/><path d="M4 20c0-4 4-6 8-6s8 2 8 6"/></svg></span>
            <span className="nav-text">Mon profil</span>
          </div>
          <div className="nav-item" onClick={() => navigate('/resources')}>
            <span className="nav-icon"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg></span>
            <span className="nav-text">Ressources</span>
          </div>
          <div className="nav-item" onClick={() => navigate('/certificate')}>
            <span className="nav-icon"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="8" r="6"/><path d="M8 14l-2 8 6-3 6 3-2-8"/></svg></span>
            <span className="nav-text">Certificat</span>
          </div>
        </nav>

        <div className="sidebar-progress-pro">
          <div className="progress-header">
            <span className="progress-title">Progression</span>
            <span className="progress-value">{Math.round(totalProgress)}%</span>
          </div>
          <div className="progress-bar-pro">
            <div className="progress-fill-pro" style={{ width: `${totalProgress}%` }} />
          </div>
        </div>

        <div className="sidebar-bottom">
          <div className="nav-item logout-item" onClick={handleLogout}>
            <span className="nav-icon"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg></span>
            <span className="nav-text">Déconnexion</span>
          </div>
        </div>
      </aside>

      <main className="dashboard-main">
        <div className="module-header">
          <button className="back-btn" onClick={() => navigate('/dashboard')}>← Retour aux programmes</button>
          <div className="module-badge">MODULE 3</div>
          <h1>💻 Claude Code : Coder sans coder</h1>
          <p className="module-subtitle">Créez des applications complètes en décrivant ce que vous voulez</p>
          <div style={{ marginTop: '20px', maxWidth: '400px' }}>
            <ProgressBar moduleId={3} showLabel={true} size="medium" />
          </div>
        </div>

        <div className="module-content">
          {/* Section 1 */}
          <section className="module-section">
            <div className="section-header-with-checkbox">
              <h2>1. Qu'est-ce que Claude Code ?</h2>
              <SectionCheckbox moduleId={3} sectionId="section1" label="J'ai compris cette section" />
            </div>

            <p>
              Claude Code est un outil révolutionnaire qui vous permet de créer des applications
              complètes simplement en décrivant ce que vous voulez. Pas besoin de savoir coder.
            </p>

            <div className="highlight-box">
              <strong>🎯 Le principe :</strong> Vous décrivez, Claude Code crée. Vous demandez des modifications,
              il les applique. C'est comme avoir un développeur personnel disponible 24h/24.
            </div>

            <div className="two-columns">
              <div className="column success">
                <h4>✅ Ce que Claude Code peut faire</h4>
                <ul className="module-list check">
                  <li>Créer des sites web complets</li>
                  <li>Développer des applications React</li>
                  <li>Créer des APIs backend</li>
                  <li>Gérer des bases de données</li>
                  <li>Corriger des bugs</li>
                  <li>Refactoriser du code existant</li>
                </ul>
              </div>
              <div className="column warning">
                <h4>⚠️ Ce dont vous avez besoin</h4>
                <ul className="module-list">
                  <li>Node.js installé sur votre machine</li>
                  <li>Un terminal (cmd, PowerShell, ou Terminal Mac)</li>
                  <li>Une idée claire de ce que vous voulez</li>
                  <li>De la patience pour itérer</li>
                </ul>
              </div>
            </div>
          </section>

          {/* Section 2 */}
          <section className="module-section">
            <div className="section-header-with-checkbox">
              <h2>2. La méthode pour décrire un projet</h2>
              <SectionCheckbox moduleId={3} sectionId="section2" label="J'ai compris cette section" />
            </div>

            <p>
              La qualité de votre description détermine la qualité du résultat.
              Voici la structure idéale pour briefer Claude Code.
            </p>

            <div className="cards-grid">
              <div className="info-card">
                <div className="card-icon">📋</div>
                <h4>1. Le "Quoi"</h4>
                <p>Type d'app, nom, description en 1-2 phrases</p>
              </div>
              <div className="info-card">
                <div className="card-icon">⚙️</div>
                <h4>2. Les fonctionnalités</h4>
                <p>Liste des 3-5 features essentielles du MVP</p>
              </div>
              <div className="info-card">
                <div className="card-icon">🚶</div>
                <h4>3. Le parcours</h4>
                <p>Comment l'utilisateur navigue dans l'app</p>
              </div>
              <div className="info-card">
                <div className="card-icon">🎨</div>
                <h4>4. Le design</h4>
                <p>Style, couleurs, ambiance visuelle</p>
              </div>
            </div>

            <h3>Exemple de brief complet</h3>
            <PromptBox title="Brief pour un dashboard analytics">
{`Crée un dashboard analytics pour suivre les ventes d'un e-commerce.

FONCTIONNALITÉS :
1. Graphique des ventes par jour/semaine/mois
2. Top 5 des produits les plus vendus
3. Carte des clients par région
4. KPIs : CA total, nombre de commandes, panier moyen

PARCOURS :
1. L'utilisateur se connecte
2. Il arrive sur le dashboard avec les KPIs en haut
3. Il peut filtrer par période (7j, 30j, 90j, custom)
4. Il peut exporter les données en CSV

DESIGN :
- Style : moderne, professionnel, fond sombre
- Couleurs : bleu #3B82F6 pour les accents, gris pour le fond
- Graphiques : utiliser Recharts ou Chart.js

TECHNIQUE :
- React avec Tailwind CSS
- Données mockées pour la démo`}
            </PromptBox>
          </section>

          {/* Section 3 - Step by Step Guide */}
          <section className="module-section">
            <div className="section-header-with-checkbox">
              <h2>3. Projet guidé : Créez votre première app</h2>
              <SectionCheckbox moduleId={3} sectionId="section3" label="J'ai fait le projet" />
            </div>

            <p>
              Suivez ce guide étape par étape pour créer votre première application
              avec Claude Code. Copiez les commandes et prompts directement.
            </p>

            <StepByStepGuide
              title="Créer une Todo App"
              description="De zéro à une application fonctionnelle"
              steps={projectSteps}
            />
          </section>

          {/* Section 4 - Playground */}
          <section className="module-section">
            <div className="section-header-with-checkbox">
              <h2>4. Créez votre propre brief</h2>
              <SectionCheckbox moduleId={3} sectionId="section4" label="J'ai créé mon brief" />
            </div>

            <p>
              Utilisez cet outil pour générer un brief complet pour votre projet.
              Remplissez les champs et copiez le résultat pour l'utiliser avec Claude Code.
            </p>

            <PromptPlayground
              title={playgroundConfig.title}
              description={playgroundConfig.description}
              templatePrompt={playgroundConfig.templatePrompt}
              variables={playgroundConfig.variables}
            />
          </section>

          {/* Section 5 */}
          <section className="module-section">
            <div className="section-header-with-checkbox">
              <h2>5. Prompts utiles pour Claude Code</h2>
              <SectionCheckbox moduleId={3} sectionId="section5" label="J'ai compris cette section" />
            </div>

            <h3>🐛 Corriger un bug</h3>
            <PromptBox title="Debug">
{`J'ai cette erreur : [COLLER L'ERREUR]

Le code concerné est dans [FICHIER].

Ce que je voulais faire : [COMPORTEMENT ATTENDU]
Ce qui se passe : [COMPORTEMENT ACTUEL]

Trouve le problème et corrige-le.`}
            </PromptBox>

            <h3>➕ Ajouter une fonctionnalité</h3>
            <PromptBox title="Nouvelle feature">
{`Dans l'application actuelle, ajoute la fonctionnalité suivante :
[DESCRIPTION DE LA FEATURE]

Elle doit :
- [COMPORTEMENT 1]
- [COMPORTEMENT 2]

Intègre-la avec le code existant sans casser ce qui fonctionne.`}
            </PromptBox>

            <h3>🎨 Modifier le design</h3>
            <PromptBox title="Modification design">
{`Modifie le design de [COMPOSANT/PAGE] :

Changements demandés :
- [CHANGEMENT 1]
- [CHANGEMENT 2]

Le style doit rester cohérent avec le reste de l'application.`}
            </PromptBox>
          </section>

          {/* Summary */}
          <section className="module-section">
            <h2>📝 Résumé du module</h2>
            <div className="summary-box">
              <ul>
                <li><strong>Claude Code = développeur IA</strong> — Décrivez, il code</li>
                <li><strong>Brief détaillé = meilleur résultat</strong> — Fonctionnalités, parcours, design</li>
                <li><strong>Itérez sans limite</strong> — Demandez des modifications précises</li>
                <li><strong>Copiez les erreurs</strong> — Claude Code les corrige</li>
                <li><strong>Testez souvent</strong> — Vérifiez chaque étape avant de continuer</li>
              </ul>
            </div>
          </section>

          {/* Quiz */}
          <section className="module-section">
            <h2>📝 Quiz de validation</h2>
            <p>Testez vos connaissances sur Claude Code et la création d'applications.</p>

            {!showQuiz ? (
              <button className="btn btn-primary" onClick={() => setShowQuiz(true)} style={{ marginTop: '16px' }}>
                🎯 Commencer le quiz
              </button>
            ) : (
              <Quiz moduleId={3} questions={quizQuestions} />
            )}
          </section>

          {/* Community */}
          <section className="module-section">
            <h2>💬 Partagez vos créations</h2>
            <p>Montrez ce que vous avez créé avec Claude Code et inspirez les autres membres.</p>
            <DiscordWidget inviteLink="https://discord.gg/votre-lien" />
          </section>

          <div className="module-navigation">
            <button className="btn btn-secondary" onClick={() => navigate('/module/2')}>← Module précédent</button>
            <button className="btn btn-primary" onClick={() => navigate('/module/4')}>Module suivant : Automatisation →</button>
          </div>
        </div>
      </main>
    </div>
  );
};

export default Module3;
